// ----------------------------------------------------------------
// <copyright company="Microsoft Corporation">
//     Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';

import { logException } from 'src/lib/ApplicationInsights';
import CorrelationVector from 'src/lib/CorrelationVector';
import ErrorBoundaryContext from 'src/lib/ErrorBoundaryContext';

interface ErrorBoundaryProps {
    onDismissBrowserWarningMessage: () => void;
    showBrowserWarningMessage: boolean;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
    state = {
        error: undefined,
        errorInfo: undefined,
        errorMessage: undefined,
    };

    componentDidMount() {
        window.addEventListener('error', (event: any) => {
            // Prevent the browser's console error message 
            event && event.preventDefault();
            event && event.stopPropagation();
        });

        window.onerror = (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {
            this.logError(error);
        };
    }
    
    static getDerivedStateFromError(error: any) {
        // Window.onerror should probably log the error, but getDerivedStateFromError seems to be required sometimes
        const correlationVector = CorrelationVector.getCV();
        logException(correlationVector, error, undefined, false);
        return {
            correlationVector,
            error,
            errorMessage: error && error.message,
        };
    }

    componentDidCatch(error: any, errorInfo: any) {
        this.logError(error, errorInfo);
    }
    
    logError = (error?: any, errorInfo?: any) => {
        if ((error && error.message) !== this.state.errorMessage) {
            const correlationVector = CorrelationVector.getCV();
            logException(correlationVector, error, errorInfo, false);
            this.setState({
                correlationVector,
                error,
                errorInfo,
                errorMessage: error && error.message,
            });
        }
    }

    onDismiss = () => {
        this.setState({
            correlationVector: undefined,
            error: undefined,
            errorInfo: undefined,
            errorMessage: undefined,
        });
    }

    render() {
        return (
            <ErrorBoundaryContext.Provider value={{...this.state, ...this.props, onDismiss: this.onDismiss}}>
                {this.props.children}
            </ErrorBoundaryContext.Provider>
        );
    }
}
